import { useState } from 'react';
import { X, Globe, ShoppingBag } from 'lucide-react';
import { useWorkspace } from '../lib/workspace.jsx';

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 62);

const PLATFORMS = [
  {
    key: 'web',
    label: 'Web App',
    Icon: Globe,
    hint: 'A site or admin you host yourself.',
  },
  {
    key: 'shopify',
    label: 'Shopify App',
    Icon: ShoppingBag,
    hint: 'An app embedded in the Shopify admin.',
  },
];

/**
 * A tenant needs a name, a kebab-case slug, and at least one allowed origin —
 * the widget's session endpoint refuses a public key presented from anywhere
 * else, so an app with an empty allowlist can never accept a conversation.
 *
 * One tenant is one *app*, not one of its customers. A Shopify app is
 * installed by thousands of stores, and each of those is a visitor here rather
 * than a tenant of its own — so nothing on this form asks which store, because
 * at this level there is no such thing. Which merchant a conversation belongs
 * to arrives with the visitor's session as metadata.
 *
 * The platform choice therefore changes advice, not identity: an embedded
 * Shopify app is served from the publisher's own domain rather than
 * `admin.shopify.com`, which is the single most common thing to get wrong in
 * the origin allowlist below.
 */
export function CreateAppDialog({ onClose }) {
  const { createApp } = useWorkspace();
  const [platform, setPlatform] = useState('web');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [origins, setOrigins] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const shopify = platform === 'shopify';
  const effectiveSlug = slugTouched ? slug : slugify(name);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const allowedOrigins = origins
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean);

      for (const origin of allowedOrigins) {
        try {
          new URL(origin);
        } catch {
          throw new Error(`"${origin}" is not an absolute URL`);
        }
      }

      await createApp({ name, slug: effectiveSlug, allowedOrigins, platform });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <form className="dialog" onMouseDown={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="dialog__head">
          <h2>New app</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <p className="login__sub">
          One tenant per app you support. Everything below it — conversations, tickets, docs,
          analytics — is scoped to this app and never visible from another.
        </p>

        <div className="field">
          <span className="field__label">WHERE THE WIDGET RUNS</span>
          <div className="choice">
            {PLATFORMS.map(({ key, label, Icon, hint }) => (
              <button
                type="button"
                key={key}
                className={`choice__option${platform === key ? ' is-active' : ''}`}
                onClick={() => setPlatform(key)}
                aria-pressed={platform === key}
              >
                <Icon size={14} className="choice__icon" />
                <span className="choice__label">{label}</span>
                <span className="choice__hint">{hint}</span>
              </button>
            ))}
          </div>
        </div>

        <label className="field">
          <span className="field__label">NAME</span>
          <input
            className="field__control"
            required
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={shopify ? 'Bundler Pro for Shopify' : 'Bundler Pro'}
            autoFocus
          />
        </label>

        <label className="field">
          <span className="field__label">SLUG</span>
          <input
            className="field__control mono"
            required
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            placeholder="bundler-pro"
          />
        </label>

        <label className="field">
          <span className="field__label">ALLOWED ORIGINS · ONE PER LINE</span>
          <textarea
            className="field__control field__control--area mono"
            rows={3}
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            placeholder="https://your-app-domain.com"
          />
        </label>

        <div className="docs__footnote">
          The widget&apos;s public key is only honoured from these origins, checked server-side as
          well as by CORS. Use the origin of the page the widget runs on
          {shopify ? (
            <>
              {' '}
              — for an embedded Shopify app that is <strong>your app&apos;s own domain</strong>,
              not <code>admin.shopify.com</code>, which is only the frame yours sits inside. Every
              store that installs your app reaches the widget through that same origin.
            </>
          ) : (
            '. '
          )}{' '}
          You can add more later in Integration.
        </div>

        {error && <div className="login__error">{error}</div>}

        <footer className="dialog__foot">
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || !name || !effectiveSlug}
          >
            {busy ? 'Creating…' : 'Create app'}
          </button>
        </footer>
      </form>
    </div>
  );
}

export default CreateAppDialog;
